import Keycloak, {KeycloakInitOptions, KeycloakLoginOptions, KeycloakLogoutOptions} from 'keycloak-js';
import {ConfigService} from "../ConfigService";
import {Subject} from "rxjs";
import {frameContent} from '../components/FrameContent';
import {
    buildUaaLogoutUrl,
    isIdTokenHintCompatibleWithKeycloakUrl,
    isUaaLogoutConfigured,
    validatePortalPostLogoutRedirectUri
} from './logoutUtils';

class UserContext {

    private keycloak: Keycloak;
    public onAuthRefreshUpdate = new Subject<void>();

    private static instance: UserContext;

    public static getInstance(): UserContext {
        if (!UserContext.instance) {
            UserContext.instance = new UserContext();
        }

        return UserContext.instance;
    }

    public init(): Promise<boolean> {
        this.keycloak = this.createKeycloakInstance();
        this.initKeycloakEventlisteners();

        const options: KeycloakInitOptions = {
            onLoad: 'check-sso',
            checkLoginIframe: true,
            silentCheckSsoRedirectUri: globalThis.location.origin + '/silent-check-sso.html',
            checkLoginIframeInterval: 5,
            enableLogging: true,
            pkceMethod: 'S256'
        };

        return new Promise<boolean>((resolve, reject) => {
            this.keycloak.init(options).then(() => {
                resolve(this.keycloak.authenticated);
            }).catch((err) => {
                console.error('[FRAME] Keycloak init error', err);
                reject(err);
            });
        });
    }

    private initKeycloakEventlisteners(){
        this.keycloak.onAuthLogout = () => {
            this.onAuthRefreshUpdate.next();
        };
        this.keycloak.onActionUpdate = (a) => {
            console.log('[FRAME] ActionUpdate', a);
        }
        this.keycloak.onAuthError = (e) => {
            console.log('[FRAME] Auth error', e);
        }
        this.keycloak.onReady = (b) => {
            console.log('[FRAME] keycloak.onReady', b);
        }

        this.keycloak.onAuthRefreshSuccess = () => {
            console.log('[FRAME] AuthRefreshSuccess');
            this.onAuthRefreshUpdate.next();
        }
        this.keycloak.onAuthRefreshError = () => {
            console.log('[FRAME] AuthRefreshError');
            this.onAuthRefreshUpdate.next();
        }

        this.keycloak.onTokenExpired = () => {
            //Uus token saab 30s aega enne kui aegunuks loetakse
            this.keycloak.updateToken(30).then((refreshed: boolean) => {
                if (refreshed) {
                    console.log('[FRAME] Token was successfully refreshed');
                } else {
                    console.log('[FRAME] Token is still valid');
                }
            }).catch(() => {
                console.error('[FRAME] Failed to refresh the token, or the session has expired');
                this.onAuthRefreshUpdate.next();
            });
        }
    }

    public login(options?: KeycloakLoginOptions): Promise<any> {
        return this.keycloak.login(options);
    }

    public logout(options?: KeycloakLogoutOptions): Promise<void> {
        const redirectUri = this.resolvePostLogoutRedirectUri(options);
        const logoutOptions: KeycloakLogoutOptions = {
            ...options,
            redirectUri
        };

        if (isUaaLogoutConfigured(ConfigService.config?.uaaLogout)) {
            return this.logoutViaUaaThenKeycloak(redirectUri, ConfigService.config.uaaLogout);
        }

        this.clearIncompatibleIdTokenHint(this.getBrowserFacingKeycloakUrl());
        return this.keycloak.logout(logoutOptions);
    }

    /**
     * Ends the UAA session first, then continues into Keycloak logout via redirect chain.
     * Redirect targets are built only from trusted configuration and same-origin portal URLs.
     */
    private logoutViaUaaThenKeycloak(redirectUri: string, uaaLogout: NonNullable<typeof ConfigService.config.uaaLogout>): Promise<void> {
        const keycloakBaseUrl = this.getBrowserFacingKeycloakUrl();
        this.clearIncompatibleIdTokenHint(keycloakBaseUrl);

        let uaaLogoutUrl: string;
        try {
            uaaLogoutUrl = buildUaaLogoutUrl(
                uaaLogout,
                ConfigService.config.keycloak,
                redirectUri,
                this.keycloak.idToken
            );
        } catch (error) {
            console.error('[FRAME] Invalid UAA logout configuration, falling back to Keycloak logout', error);
            this.clearIncompatibleIdTokenHint(this.getBrowserFacingKeycloakUrl());
            return this.keycloak.logout({redirectUri});
        }

        console.info('[FRAME] Starting UAA front-channel logout, redirecting to Keycloak logout');
        globalThis.location.assign(uaaLogoutUrl);
        return Promise.resolve();
    }

    private getBrowserFacingKeycloakUrl(): string {
        return ConfigService.config.uaaLogout?.keycloakUrl ?? ConfigService.config.keycloak.url;
    }

    private createKeycloakInstance(): Keycloak {
        return new Keycloak({
            ...ConfigService.config.keycloak,
            url: this.getBrowserFacingKeycloakUrl()
        });
    }

    /**
     * Drops an ID token hint when its issuer does not match the browser-facing Keycloak URL.
     * Keycloak can still end the session using its own session cookie.
     */
    private clearIncompatibleIdTokenHint(keycloakBaseUrl: string): void {
        if (this.keycloak.idToken && !isIdTokenHintCompatibleWithKeycloakUrl(this.keycloak.idToken, keycloakBaseUrl)) {
            this.keycloak.idToken = undefined;
        }
    }

    private resolvePostLogoutRedirectUri(options?: KeycloakLogoutOptions): string {
        const configuredRedirectUri = options?.redirectUri ?? frameContent.getActiveSiteUrl();
        return validatePortalPostLogoutRedirectUri(configuredRedirectUri);
    }

    public getToken() {
        return this.keycloak.token;
    }

    public getTokenParsed(): any {
        return this.keycloak.tokenParsed;
    }

    public isAuthenticated() {
        return this.keycloak.authenticated;
    }
}

export const userContext = UserContext.getInstance();
